import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  RefreshCw, CheckCircle, AlertTriangle, Clock, Zap, ChevronUp, ChevronDown,
  ChevronsUpDown, Flame, ChevronRight, Loader2, LayoutGrid, X,
} from "lucide-react";
import { toast } from "sonner";
import { poolReadinessCold, prewarmStart, prewarmStatus } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token) {
  return fetch(`${API}/api${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ── Status badge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = {
    warm:     { bg: "#d1fae5", color: "#065f46", label: "Warm",     icon: CheckCircle },
    building: { bg: "#fef3c7", color: "#92400e", label: "Building", icon: AlertTriangle },
    shell:    { bg: "#fee2e2", color: "#991b1b", label: "Shell",    icon: Clock },
    cold:     { bg: "#fff7ed", color: "#c2410c", label: "Cold",     icon: Zap },
    warming:  { bg: "#eff6ff", color: "#1d4ed8", label: "Warming…", icon: Flame },
    done:     { bg: "#d1fae5", color: "#065f46", label: "Warmed",   icon: CheckCircle },
    error:    { bg: "#fee2e2", color: "#991b1b", label: "Error",    icon: AlertTriangle },
  }[status] || { bg: "#f3f4f6", color: "#6b7280", label: status, icon: Clock };
  const Icon = cfg.icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-xs whitespace-nowrap" style={{ background: cfg.bg, color: cfg.color }}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ── Progress bar ───────────────────────────────────────────────────────────────
function ProgressBar({ value, max, status }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const barColor = status === "warm" ? "#10b981" : status === "building" ? "#f59e0b" : "#ef4444";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
      </div>
      <span className="text-xs font-mono text-gray-600 whitespace-nowrap">{value}/{max}</span>
    </div>
  );
}

// ── Summary tile ───────────────────────────────────────────────────────────────
function SummaryTile({ label, count, bg, color, icon: Icon, description, onClick, clickLabel }) {
  return (
    <div className="rounded-xl p-4 flex items-start gap-3" style={{ background: bg, border: `1px solid ${color}30` }}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-2xl font-bold" style={{ color }}>{count}</div>
        <div className="text-sm font-semibold text-gray-700">{label}</div>
        <div className="text-xs text-gray-400 mt-0.5">{description}</div>
        {onClick && (
          <button onClick={onClick} className="mt-1.5 text-xs font-semibold underline" style={{ color }}>
            {clickLabel}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Sort helpers ───────────────────────────────────────────────────────────────
const STATUS_RANK = { warm: 3, building: 2, shell: 1, cold: 0 };
const SORT_FNS = {
  grade:      (r) => r.grade ?? 0,
  subject:    (r) => r.subject || "",
  chapter:    (r) => r.chapter || "",
  sub_topic:  (r) => r.sub_topic || "",
  status:     (r) => STATUS_RANK[r.status] ?? -1,
  generated:  (r) => r.generated_count ?? 0,
  total:      (r) => r.total_questions ?? 0,
  reshuffled: (r) => r.last_reshuffled || "",
};

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronsUpDown size={11} className="ml-1 opacity-40 inline" />;
  return sortDir === "asc"
    ? <ChevronUp size={11} className="ml-1 text-gray-700 inline" />
    : <ChevronDown size={11} className="ml-1 text-gray-700 inline" />;
}

// ── Pre-warm button ────────────────────────────────────────────────────────────
function PrewarmBtn({ row, state, onPrewarm, label = "Pre-warm" }) {
  const testId = label === "Pre-warm"
    ? `prewarm-btn-${row.sub_topic_id}`
    : `continue-warm-btn-${row.sub_topic_id}`;
  if (state?.status === "running") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium px-2 py-1">
        <Loader2 size={11} className="animate-spin" /> Warming…
      </span>
    );
  }
  if (state?.status === "done") return null; // status badge handles it
  if (state?.status === "error") {
    return (
      <button
        onClick={() => onPrewarm(row)}
        className="text-xs text-red-600 underline px-2 py-1"
        title={state.error}
        data-testid={`retry-prewarm-${row.sub_topic_id}`}
      >
        Retry
      </button>
    );
  }
  return (
    <button
      onClick={() => onPrewarm(row)}
      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 transition-colors"
      data-testid={testId}
    >
      <Flame size={11} />
      {label}
    </button>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function PoolReadiness({ token }) {
  const [searchParams] = useSearchParams();
  const navigate        = useNavigate();

  // Scope from URL params
  const scopeBoard   = searchParams.get("board")   || "";
  const scopeGrade   = searchParams.get("grade")   || "";
  const scopeSubject = searchParams.get("subject") || "";
  const isScoped     = !!(scopeBoard || scopeGrade || scopeSubject);

  // Parse "Grade 5" → "5" for pool-readiness integer grade filter
  const parsedGrade = scopeGrade
    ? String(scopeGrade).replace(/^Grade\s*/i, "").trim()
    : "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters — initialise from URL scope params if present
  const [filterGrade, setFilterGrade] = useState(parsedGrade);
  const [filterSubject, setFilterSubject] = useState(scopeSubject);
  const [filterStatus, setFilterStatus] = useState("");

  // Base filter options (all grades/subjects from initial unfiltered load)
  const [baseFilterOptions, setBaseFilterOptions] = useState({ grades: [], subjects: [] });

  // Sort
  const [sortCol, setSortCol] = useState("status");
  const [sortDir, setSortDir] = useState("asc");

  // Cold sub-topics state
  const [coldData, setColdData] = useState(null);
  const [coldLoading, setColdLoading] = useState(false);
  const [showCold, setShowCold] = useState(false);
  const [coldFilterStatus, setColdFilterStatus] = useState("all"); // "all" | "cold"
  const [coldFilterGrade, setColdFilterGrade] = useState("");
  const [coldFilterSubject, setColdFilterSubject] = useState("");

  // Pre-warm state: { [sub_topic_id]: { jobId, status, error } }
  const [prewarmStates, setPrewarmStates] = useState({});
  // Warm-All job
  const [warmAllJobId, setWarmAllJobId] = useState(null);
  const [warmAllProgress, setWarmAllProgress] = useState({ completed: 0, total: 0 });
  const warmAllIntervalRef = useRef(null);

  // Load main pool readiness
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterGrade) params.set("grade", filterGrade);
      if (filterSubject) params.set("subject", filterSubject);
      const qs = params.toString() ? `?${params}` : "";
      const res = await apiFetch(`/admin/cr/pool-readiness${qs}`, token);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setData(result);
      // Capture full options on unfiltered load so grade dropdown never shrinks
      if (!filterGrade && !filterSubject) {
        setBaseFilterOptions(result.filter_options ?? { grades: [], subjects: [] });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterGrade, filterSubject]);

  useEffect(() => { load(); }, [load]);

  // Load cold sub-topics
  const loadCold = useCallback(async () => {
    setColdLoading(true);
    try {
      const result = await poolReadinessCold(filterGrade || null, filterSubject || null, token);
      setColdData(result);
      setShowCold(true);
    } catch (e) {
      toast.error("Failed to load cold sub-topics: " + e.message);
    } finally {
      setColdLoading(false);
    }
  }, [token, filterGrade, filterSubject]);

  // Warm-All polling
  useEffect(() => {
    if (!warmAllJobId) return;
    warmAllIntervalRef.current = setInterval(async () => {
      try {
        const status = await prewarmStatus(warmAllJobId, token);
        setWarmAllProgress({ completed: status.completed, total: status.total });
        if (status.status === "done") {
          clearInterval(warmAllIntervalRef.current);
          setWarmAllJobId(null);
          const warmed = (status.results || []).filter(r => r.status === "warm" || r.status === "building").length;
          toast.success(`Warm All complete! ${warmed}/${status.total} sub-topics processed.`);
          load();
          if (showCold) loadCold();
        }
      } catch {
        clearInterval(warmAllIntervalRef.current);
        setWarmAllJobId(null);
      }
    }, 2000);
    return () => clearInterval(warmAllIntervalRef.current);
  }, [warmAllJobId]);

  const handleSort = (col) => {
    setSortCol(col);
    setSortDir(d => sortCol === col ? (d === "asc" ? "desc" : "asc") : "asc");
  };

  // ── Grade change — cascades: reset subject ───────────────────────────────────
  const handleGradeChange = (val) => {
    setFilterGrade(val);
    setFilterSubject(""); // subject must reset when grade changes
  };

  // ── Pre-warm single row ──────────────────────────────────────────────────────
  const handlePrewarm = async (row) => {
    setPrewarmStates(prev => ({
      ...prev,
      [row.sub_topic_id]: { status: "running" },
    }));
    try {
      const result = await prewarmStart(
        [{ grade: row.grade, subject: row.subject, chapter: row.chapter, sub_topic: row.sub_topic }],
        token,
      );
      const jobId = result.job_id;
      // Poll for this row
      const interval = setInterval(async () => {
        try {
          const status = await prewarmStatus(jobId, token);
          if (status.status === "done") {
            clearInterval(interval);
            const r = status.results?.[0];
            const finalStatus = r?.status || "done";
            setPrewarmStates(prev => ({
              ...prev,
              [row.sub_topic_id]: { status: finalStatus === "error" ? "error" : "done", error: r?.error },
            }));
            if (finalStatus !== "error") {
              const action = row.status === "building" ? "Continue Warming" : "Pre-warm";
              toast.success(`${action} done: ${row.sub_topic} (+${r?.questions_added ?? 0} questions)`);
              load();
              if (showCold) loadCold();
            } else {
              toast.error(`Pre-warm failed: ${r?.error}`);
            }
          }
        } catch {
          clearInterval(interval);
          setPrewarmStates(prev => ({
            ...prev,
            [row.sub_topic_id]: { status: "error", error: "Lost connection" },
          }));
        }
      }, 2000);
    } catch (e) {
      toast.error("Failed to start pre-warm: " + e.message);
      setPrewarmStates(prev => ({
        ...prev,
        [row.sub_topic_id]: { status: "error", error: e.message },
      }));
    }
  };

  // ── Warm All (cold + shell, filtered) ────────────────────────────────────────
  const handleWarmAll = async () => {
    const coldRows = coldData?.cold_sub_topics || [];
    const shellRows = (data?.pools || []).filter(p => p.status === "shell");
    // Apply current grade/subject filters
    const filteredCold = coldRows.filter(r =>
      (!filterGrade || String(r.grade) === String(filterGrade)) &&
      (!filterSubject || r.subject === filterSubject)
    );
    const allRows = [...filteredCold, ...shellRows];
    if (allRows.length === 0) {
      toast.info("No cold or shell sub-topics to warm with current filters.");
      return;
    }
    const items = allRows.slice(0, 200).map(r => ({
      grade: r.grade, subject: r.subject, chapter: r.chapter, sub_topic: r.sub_topic,
    }));
    try {
      const result = await prewarmStart(items, token);
      setWarmAllJobId(result.job_id);
      setWarmAllProgress({ completed: 0, total: result.total });
      toast.info(`Warming ${result.total} sub-topic${result.total !== 1 ? "s" : ""}…`);
    } catch (e) {
      toast.error("Failed to start Warm All: " + e.message);
    }
  };

  // ── Derived data ─────────────────────────────────────────────────────────────
  const pools = data?.pools ?? [];
  const summary = data?.summary ?? {};
  const filterOptions = data?.filter_options ?? {};

  const visiblePools = filterStatus ? pools.filter(p => p.status === filterStatus) : pools;
  const sortedPools = [...visiblePools].sort((a, b) => {
    const fn = SORT_FNS[sortCol];
    if (!fn) return 0;
    const va = fn(a), vb = fn(b);
    const cmp = typeof va === "string"
      ? va.localeCompare(vb, undefined, { sensitivity: "base" })
      : va - vb;
    return sortDir === "asc" ? cmp : -cmp;
  });

  const allColdRows = coldData?.cold_sub_topics ?? [];
  // Fix 3a: merge shell sub-topics (pool exists, 0 questions) into the cold section
  const shellRows = pools.filter(p => p.status === "shell");
  const allColdAndShellRows = [
    ...allColdRows,
    ...shellRows.filter(s =>
      !allColdRows.some(c => c.sub_topic_id === s.sub_topic_id)
    ),
  ];
  const coldGrades = [...new Set(allColdAndShellRows.map(r => String(r.grade)))].sort((a, b) => Number(a) - Number(b));
  const coldSubjects = [...new Set(allColdAndShellRows.map(r => r.subject))].sort();
  const coldRows = allColdAndShellRows.filter(r =>
    (!coldFilterGrade || String(r.grade) === coldFilterGrade) &&
    (!coldFilterSubject || r.subject === coldFilterSubject)
  );
  const coldTotal = coldData?.total ?? summary.cold ?? 0;
  const isWarmingAll = !!warmAllJobId;

  // ── Styles ───────────────────────────────────────────────────────────────────
  const thBase = {
    padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
    letterSpacing: "0.05em", color: "#6b7280", borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb", whiteSpace: "nowrap", userSelect: "none", cursor: "pointer",
  };
  const tdBase = { padding: "11px 14px", fontSize: 13, borderBottom: "1px solid #f3f4f6", verticalAlign: "middle" };

  const Th = ({ col, align = "left", children }) => (
    <th style={{ ...thBase, textAlign: align }} onClick={() => col && handleSort(col)} data-testid={`sort-${col}`}>
      {children}{col && <SortIcon col={col} sortCol={sortCol} sortDir={sortDir} />}
    </th>
  );

  const shellCount = pools.filter(p => p.status === "shell").length;
  // Fix 3a: canWarmAll now uses coldRows which already includes shells
  const canWarmAll = coldRows.length > 0;

  return (
    <div className="p-6 max-w-screen-xl" data-testid="pool-readiness-page">

      {/* Scoped-mode banner */}
      {isScoped && (
        <div
          className="mb-5 px-4 py-3 rounded-xl border border-indigo-200 bg-indigo-50"
          data-testid="scoped-banner-pr"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-indigo-700 text-sm flex-wrap">
              <LayoutGrid className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold">Fuel Registry scope:</span>
              {scopeBoard   && <span className="bg-indigo-100 px-2 py-0.5 rounded font-mono text-xs">{scopeBoard}</span>}
              {scopeBoard   && (scopeGrade || scopeSubject) && <span className="text-indigo-400">·</span>}
              {scopeGrade   && <span className="text-indigo-600">{scopeGrade}</span>}
              {scopeGrade   && scopeSubject && <span className="text-indigo-400">·</span>}
              {scopeSubject && <span className="text-indigo-600">{scopeSubject}</span>}
            </div>
            <button
              onClick={() => navigate("/admin/pool-readiness")}
              className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-medium transition-colors"
              data-testid="clear-scope-pr-btn"
            >
              <X className="w-3.5 h-3.5" /> Clear scope
            </button>
          </div>
          {scopeBoard && (
            <p className="mt-1.5 text-xs text-indigo-500" data-testid="board-scope-notice">
              Board filter applies to Dataset Health only.
            </p>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pool Readiness</h1>
          <p className="text-sm text-gray-500 mt-1">
            Test question pool warm/building/cold status across all grades &amp; subjects
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isWarmingAll && (
            <span className="text-sm text-blue-700 font-medium flex items-center gap-1.5 bg-blue-50 px-3 py-1.5 rounded-lg">
              <Loader2 size={14} className="animate-spin" />
              Warming {warmAllProgress.completed}/{warmAllProgress.total}…
            </span>
          )}
          {canWarmAll && !isWarmingAll && (
            <button
              onClick={handleWarmAll}
              data-testid="warm-all-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-colors"
            >
              <Flame size={14} />
              Warm All{" "}
              {filterGrade || filterSubject
                ? `(${allColdRows.filter(r =>
                    (!filterGrade || String(r.grade) === String(filterGrade)) &&
                    (!filterSubject || r.subject === filterSubject)
                  ).length + shellCount} filtered)`
                : `(${coldTotal + shellCount})`}
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ background: loading ? "#9ca3af" : "#2563eb" }}
            data-testid="refresh-pools-btn"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="pool-error">
          Error: {error}
        </div>
      )}

      {/* Summary tiles */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryTile label="Warm" count={summary.warm ?? 0} bg="#f0fdf4" color="#16a34a" icon={CheckCircle} description="Ready — serves in <2s" />
          <SummaryTile label="Building" count={summary.building ?? 0} bg="#fffbeb" color="#d97706" icon={AlertTriangle} description="Filling up — will be slow" />
          <SummaryTile
            label="Cold"
            count={summary.cold ?? 0}
            bg="#fff7ed"
            color="#ea580c"
            icon={Zap}
            description="Never touched — ~60s first use"
            onClick={!showCold ? loadCold : undefined}
            clickLabel={coldLoading ? "Loading…" : "Show cold rows"}
          />
          <SummaryTile label="Total Possible" count={summary.total_possible ?? 0} bg="#f8fafc" color="#475569" icon={ChevronsUpDown} description={`${summary.total_existing ?? 0} initialised`} />
        </div>
      )}

      {/* Legend */}
      <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 flex flex-wrap gap-4">
        <span><strong>Warm</strong> — floor hit, questions in rotation. Fast serving.</span>
        <span><strong>Building</strong> — some questions generated, floor not yet reached.</span>
        <span><strong>Shell</strong> — pool record exists but 0 questions yet.</span>
        <span><strong>Cold</strong> — no pool record. First generate calls Claude (~60s).</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={filterGrade} onChange={e => handleGradeChange(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-grade">
          <option value="">All Grades</option>
          {(baseFilterOptions.grades.length > 0 ? baseFilterOptions.grades : (filterOptions.grades || [])).map(g => <option key={g} value={g}>Grade {g}</option>)}
        </select>
        <select value={filterSubject} onChange={e => setFilterSubject(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-subject">
          <option value="">All Subjects</option>
          {(filterOptions.subjects || baseFilterOptions.subjects || []).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-status">
          <option value="">All Statuses</option>
          <option value="warm">Warm</option>
          <option value="building">Building</option>
          <option value="shell">Shell</option>
        </select>
        {(filterGrade || filterSubject || filterStatus) && (
          <button onClick={() => { handleGradeChange(""); setFilterStatus(""); }} className="text-xs text-blue-600 underline" data-testid="clear-filters-btn">
            Clear filters
          </button>
        )}
        <span className="ml-auto text-xs text-gray-400" data-testid="pool-count">
          {sortedPools.length} pool{sortedPools.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Existing pools table */}
      {loading && !data ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading pools…</div>
      ) : sortedPools.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm" data-testid="no-pools-msg">
          {data ? "No pools match the selected filters." : "No pool data available."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid #e5e7eb", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <table className="w-full border-collapse" data-testid="pool-table">
            <thead>
              <tr>
                <Th col="grade">Grade</Th>
                <Th col="subject">Subject</Th>
                <Th col="chapter">Chapter</Th>
                <Th col="sub_topic">Sub-topic</Th>
                <Th col="status" align="center">Status</Th>
                <Th col="generated" align="center">Generated / Floor</Th>
                <Th col="total" align="center">Total</Th>
                <Th col={null} align="center">Fuel Qs</Th>
                <Th col="reshuffled" align="center">Last Reshuffled / Updated</Th>
                <Th col={null} align="center">Action</Th>
              </tr>
            </thead>
            <tbody>
              {sortedPools.map((pool, idx) => {
                // Fix 3b: warm → last_reshuffled; building → last_updated, fallback to created_at for older pools
                const tsSource = pool.status === "warm"
                  ? pool.last_reshuffled
                  : (pool.last_updated || pool.created_at);
                const reshuffled = tsSource
                  ? new Date(tsSource).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                  : "—";
                const pwState = prewarmStates[pool.sub_topic_id];
                const displayStatus = pwState?.status === "done" ? "warm"
                  : pwState?.status === "running" ? "warming"
                  : pool.status;
                return (
                  <tr key={pool.sub_topic_id} style={{ background: idx % 2 === 0 ? "#ffffff" : "#fafafa" }} data-testid={`pool-row-${pool.sub_topic_id}`}>
                    <td style={tdBase}>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: "#eff6ff", color: "#1d4ed8" }}>G{pool.grade}</span>
                    </td>
                    <td style={{ ...tdBase, fontWeight: 500 }}>{pool.subject}</td>
                    <td style={{ ...tdBase, color: "#6b7280" }}>{pool.chapter}</td>
                    <td style={{ ...tdBase, maxWidth: 240, fontSize: 12 }}>{pool.sub_topic}</td>
                    <td style={{ ...tdBase, textAlign: "center" }}>
                      <StatusBadge status={displayStatus} />
                    </td>
                    <td style={{ ...tdBase, textAlign: "center" }}>
                      <ProgressBar value={pool.generated_count ?? 0} max={pool.pool_floor ?? 30} status={pool.status} />
                    </td>
                    <td style={{ ...tdBase, textAlign: "center", fontWeight: 600 }}>{pool.total_questions ?? 0}</td>
                    <td style={{ ...tdBase, textAlign: "center", color: "#6b7280" }}>{pool.fuel_count ?? 0}</td>
                    <td style={{ ...tdBase, textAlign: "center", color: "#9ca3af", fontSize: 12 }}>{reshuffled}</td>
                    <td style={{ ...tdBase, textAlign: "center" }}>
                      {(pool.status === "shell" || pool.status === "building") && (
                        <PrewarmBtn
                          row={pool}
                          state={pwState}
                          onPrewarm={handlePrewarm}
                          label={pool.status === "building" ? "+ Warm" : "Pre-warm"}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cold (& Shell) sub-topics section */}
      {(showCold || coldLoading) && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap size={16} className="text-orange-500" />
              <h2 className="text-base font-bold text-gray-800">
                {shellRows.length > 0 ? "Cold & Shell" : "Cold"} Sub-topics{" "}
                {(coldData || shellRows.length > 0) ? `(${coldRows.length})` : ""}
              </h2>
              <span className="text-xs text-gray-400">
                {shellRows.length > 0
                  ? "— Cold: no pool record yet · Shell: pool exists, 0 questions"
                  : "— no pool record yet, will be slow on first use"}
              </span>
            </div>
            <button
              onClick={() => setShowCold(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Hide
            </button>
          </div>
          {/* Cold section filters */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select
              value={coldFilterGrade}
              onChange={e => setColdFilterGrade(e.target.value)}
              className="border border-orange-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-orange-300"
              data-testid="cold-filter-grade"
            >
              <option value="">All Grades</option>
              {coldGrades.map(g => <option key={g} value={g}>Grade {g}</option>)}
            </select>
            <select
              value={coldFilterSubject}
              onChange={e => setColdFilterSubject(e.target.value)}
              className="border border-orange-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-orange-300"
              data-testid="cold-filter-subject"
            >
              <option value="">All Subjects</option>
              {coldSubjects.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {(coldFilterGrade || coldFilterSubject) && (
              <button
                onClick={() => { setColdFilterGrade(""); setColdFilterSubject(""); }}
                className="text-xs text-orange-600 underline"
                data-testid="cold-clear-filters-btn"
              >
                Clear
              </button>
            )}
          </div>

          {coldLoading ? (
            <div className="text-center py-8 text-gray-400 text-sm flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading cold sub-topics…
            </div>
          ) : coldRows.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No cold or shell sub-topics found for current filters.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid #fed7aa", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <table className="w-full border-collapse" data-testid="cold-table">
                <thead>
                  <tr>
                    {["Grade", "Subject", "Chapter", "Sub-topic", "Status", "Action"].map(h => (
                      <th key={h} style={{ ...thBase, background: "#fff7ed" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {coldRows.map((row, idx) => {
                    const uniqueKey = `${row.sub_topic_id}-${idx}`;
                    const pwState = prewarmStates[row.sub_topic_id];
                    // Fix 3a: preserve the original status (cold or shell) instead of forcing "cold"
                    const baseStatus = row.status === "shell" ? "shell" : "cold";
                    const displayStatus = pwState?.status === "done" ? "done"
                      : pwState?.status === "running" ? "warming"
                      : pwState?.status === "error" ? "error"
                      : baseStatus;
                    return (
                      <tr key={uniqueKey} style={{ background: idx % 2 === 0 ? "#ffffff" : "#fffbf7" }} data-testid={`cold-row-${row.sub_topic_id}`}>
                        <td style={tdBase}>
                          <span className="px-2 py-0.5 rounded text-xs font-semibold" style={{ background: "#eff6ff", color: "#1d4ed8" }}>G{row.grade}</span>
                        </td>
                        <td style={{ ...tdBase, fontWeight: 500 }}>{row.subject}</td>
                        <td style={{ ...tdBase, color: "#6b7280" }}>{row.chapter}</td>
                        <td style={{ ...tdBase, maxWidth: 240, fontSize: 12 }}>{row.sub_topic}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>
                          <StatusBadge status={displayStatus} />
                        </td>
                        <td style={{ ...tdBase, textAlign: "center" }}>
                          {displayStatus !== "done" && (
                            <PrewarmBtn row={row} state={pwState} onPrewarm={handlePrewarm} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
